import { Component, ContentChild, EventEmitter, Input, OnInit, Output, TemplateRef } from "@angular/core";
import { FormControl } from "@angular/forms";
import { BehaviorSubject, of as observableOf } from "rxjs";
import { debounceTime, map, switchMap, tap } from "rxjs/operators";
import { LifecycleHandlerComponent } from "../../../shared";
import { SearchEntityService } from "./services";
import { AutocompleteDefinition } from "./models";

@Component({
  selector: "cim-autocomplete",
  templateUrl: "./autocomplete.component.html",
  styleUrls: ["./autocomplete.component.scss"]
})
export class AutocompleteComponent<T = any> extends LifecycleHandlerComponent implements OnInit {
  readonly searchControl: FormControl = new FormControl();
  readonly additionalBehaviour$ = new BehaviorSubject<TemplateRef<any>>(null);

  @ContentChild(TemplateRef, { static: true })
  set additionalBehaviour(value: TemplateRef<any>) {
    this.additionalBehaviour$.next(value);
  }

  @Input() autocompleteDefinition: AutocompleteDefinition<T>;
  @Input() required = false;
  suggestions: Array<T>;
  selectedEntities = new Array<T>();

  @Output() selectionChange = new EventEmitter();

  constructor(private readonly searchEntityService: SearchEntityService) {
    super();
  }

  /*
   * Set the initial selected entity from plugin then listen to the user's inputs to search for entities
   */
  ngOnInit() {
    if (this.autocompleteDefinition.initialValues$) {
      this.subscriptions.push(this.autocompleteDefinition.initialValues$.subscribe(entities => {
        if (entities) {
          entities.forEach(entity => this.selectedEntities.push(entity));
        }
      }));
    }
    this.addFormChangeListener();
  }

  addFormChangeListener() {
    this.subscriptions.push(this.searchControl.valueChanges.pipe(
      map(value => value.trim()),
      debounceTime(200),
      switchMap(value => {
        if (value.length > 2) {
          return this.searchEntityService.getEntitiesByTyped(
            this.autocompleteDefinition.queryBuilder,
            this.autocompleteDefinition.dataMapper,
            value
          );
        } else {
          return observableOf([]);
        }
      }),
      tap(suggestions => {
        this.suggestions = suggestions;
      })
    ).subscribe()
    );
  }

  /*
  * On element selection - Send the current selection in the "selectionChange" EventEmitter and reset the suggestions
  */
  selectEntity(element: T): void {
    if (this.selectedEntities.length === this.autocompleteDefinition.maximumNumberOfSelectedEntities) {
      this.selectedEntities.splice(-1);
    }

    if (this.autocompleteDefinition.selectMap) {
      element = this.autocompleteDefinition.selectMap(element);
    }

    this.selectedEntities.push(element);
    this.selectionChange.emit(this.selectedEntities);
    this.searchControl.setValue("");
    this.suggestions = [];
  }

  removeSelectedEntity(entity: T): void {
    const index = this.selectedEntities.indexOf(entity);
    if (index !== -1) {
      this.selectedEntities.splice(index, 1);
      this.selectionChange.emit(this.selectedEntities);
    }
  }

  updateSelectedEntity(entity: T): void {
    const identifier = this.autocompleteDefinition.identifier;
    const index = this.selectedEntities.findIndex(selectedEntity => selectedEntity[identifier] === entity[identifier]);
    if (index >= 0) {
      this.selectedEntities[index] = entity;
    }
  }

  hasSelection() {
    return this.selectedEntities && this.selectedEntities.length;
  }
}
